/* @flow */

import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import styles from './AuthenticationBase.style';

import AuthenticationFormHeader from './AuthenticationFormHeader';
import AuthenticationPasswordToggle from './AuthenticationPasswordToggle';
import LazyPasswordSupportMain from 'src/shared/app/authentication/component/support/password/LazyPasswordSupportMain';
import messages from 'src/shared/app/authentication/l10n/authenticationL10n';
import { passwordFieldMaxLength } from 'src/shared/app/authentication/settings/authenticationSettings';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import Markdown from 'src/shared/app/base/component/text/Markdown';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { passwordSupportRoute } from 'src/shared/app/base/route/setting/routeSettings';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';
import NoteText from 'src/shared/ui/component/text/NoteText';

type Props = {
  type: ?string,
  legend: ?string,
  label: ?string,
  placeholder?: string,
  note: ?string,
  submitCta: string,
  handleSubmit: (Object) => Promise<any>,
  isPasswordSet: boolean,
  forgotPasswordURL: ?string,
};

const useStyles = createUseStyles(styles);

const AuthenticationPasswordForm = ({
  type: propType,
  legend,
  label,
  placeholder,
  note,
  submitCta,
  handleSubmit,
  isPasswordSet,
  forgotPasswordURL,
}: Props): React.Node => {
  const type = propType || 'password';
  const isTypePassword = type === 'password';

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  /*
  |----------------------------------------------------------------------------
  | Hooks
  |----------------------------------------------------------------------------
  */

  const [passwordSupportPath] = useRoutes(passwordSupportRoute);
  const intl = useIntl();

  const passwordSchema = Yup.string().required(
    isTypePassword
      ? intl.formatMessage(messages.passwordRequiredError)
      : undefined,
  );

  const schema = Yup.object().shape({
    password: passwordSchema,
  });

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    LazyPasswordSupportMain.preload();
  }, []);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onPasswordToggle = () => setIsPasswordVisible(!isPasswordVisible);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const toggleElt = isTypePassword && (
    <AuthenticationPasswordToggle
      layoutClass={classes.toggle}
      isPasswordVisible={isPasswordVisible}
      handleToggle={onPasswordToggle}
    />
  );

  const helperElt = isTypePassword && isPasswordSet && (
    <DiscreteLink href={forgotPasswordURL || passwordSupportPath}>
      {intl.formatMessage(messages.forgotPasswordLink)}
    </DiscreteLink>
  );

  const noteElt = note && (
    <NoteText layoutClass={classes.footer}>
      <Markdown>{note}</Markdown>
    </NoteText>
  );

  return (
    <Formik
      initialValues={{ password: '' }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting, setStatus }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          const [error] = Object.values(errors || {});

          if (error) {
            setStatus(error);
          }
        })
      }
    >
      {({ status, dirty, isSubmitting }) => (
        <Form className={classes.form} noValidate>
          <AuthenticationFormHeader withError={!!status}>
            {status || legend}
          </AuthenticationFormHeader>
          <div className={classes.group}>
            <Field
              id="password"
              name="password"
              type={isPasswordVisible ? 'text' : type}
              component={FormikFormGroup}
              componentOpts={{
                label: label || intl.formatMessage(messages.passwordLabel),
              }}
              maxLength={passwordFieldMaxLength}
              placeholder={placeholder}
              required
              autoComplete="off"
              autoFocus
            />
            {toggleElt}
          </div>
          <div className={classes.submit}>
            <SecondaryButton isDisabled={!dirty} isSubmitting={isSubmitting}>
              {submitCta}
            </SecondaryButton>
          </div>
          <div className={classes.support}>{helperElt}</div>
          {noteElt}
        </Form>
      )}
    </Formik>
  );
};

export default compose(React.memo)(AuthenticationPasswordForm);
